import React from "react";
import ReactDOM from "react-dom";
import { MoreVertical, Edit, Trash2, Eye } from "lucide-react";
import { Agent, AgentType } from "../../api/services/agents/type";
import { useNavigate } from "react-router-dom";

interface AgentTableProps {
  agents: Agent[];
  onEdit?: (agent: Agent) => void;
  onDelete?: (agentId: string) => void;
  onView?: (agent: Agent) => void;
  isLoading?: boolean;
}

const getAgentTypeBadge = (type: AgentType) => {
  const baseClasses =
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

  switch (type) {
    case "JUNKET":
      return `${baseClasses} bg-purple-100 text-purple-800`;
    case "NORMAL":
    default:
      return `${baseClasses} bg-blue-100 text-blue-800`;
  }
};

export const AgentTable: React.FC<AgentTableProps> = ({
  agents,
  onEdit,
  onDelete,
  onView,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const buttonRefs = React.useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );

  const handleMenuToggle = (
    agentId: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (openMenuId === agentId) {
      setOpenMenuId(null);
      setDropdownPosition(null);
    } else {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192 + window.scrollX, // 192px = w-48 in tailwind
      });
      setOpenMenuId(agentId);
    }
  };

  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
    setDropdownPosition(null);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".dropdown-menu") &&
        !target.closest(".dropdown-trigger")
      ) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openMenuId]);

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading agents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg overflow-hidden">
      {agents.length > 0 && !isLoading && (
        <div className="px-4 py-2 bg-slate-700/50 border-b border-slate-700">
          <p className="text-xs text-slate-400">
            💡 Click on any row to view agent details
          </p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-slate-700 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap">
                Agent ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap hidden sm:table-cell">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                Phone
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                Commission
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                Share
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-300 uppercase tracking-wider sticky right-0 bg-slate-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {agents.map((agent) => (
              <tr
                key={agent.id}
                className="hover:bg-slate-700 transition-all duration-200 cursor-pointer group border-b border-slate-700 hover:shadow-md hover:scale-[1.01]"
                onClick={(e) => {
                  // Don't navigate if clicking on the actions button or menu
                  if (!(e.target as HTMLElement).closest(".actions-menu")) {
                    onView?.(agent);
                  }
                  navigate(`/agents/${agent.id}`);
                }}
              >
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300 group-hover:text-slate-100">
                  {agent.agentId}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          {agent.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors">
                        {agent.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                  <div className="text-sm text-slate-300">{agent.email}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300 hidden md:table-cell">
                  {agent.phone}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={getAgentTypeBadge(agent.agentType)}>
                    {agent.agentType.charAt(0).toUpperCase() +
                      agent.agentType.slice(1)}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300 hidden lg:table-cell">
                  {agent.commissionPercent}%
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-300 hidden lg:table-cell">
                  {agent.agentType === "JUNKET"
                    ? `${agent.sharePercentage || 0}%`
                    : "-"}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium sticky right-0 bg-slate-800 group-hover:bg-slate-700">
                  <button
                    ref={(el) => {
                      buttonRefs.current[agent.id] = el;
                    }}
                    className="p-1 hover:bg-slate-600 rounded dropdown-trigger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuToggle(agent.id, e);
                    }}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {agents.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-slate-400">No agents found</div>
        </div>
      )}

      {/* Portal-rendered Dropdown Menu */}
      {openMenuId &&
        dropdownPosition &&
        ReactDOM.createPortal(
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                setOpenMenuId(null);
                setDropdownPosition(null);
              }}
            />
            <div
              className="dropdown-menu fixed w-48 bg-slate-700 rounded-md shadow-lg z-50 border border-slate-600"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
              }}
            >
              <div className="py-1">
                {onView && (
                  <button
                    className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      const agent = agents.find((a) => a.id === openMenuId);
                      if (agent) {
                        handleAction(() => onView(agent));
                      }
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </button>
                )}
                {onEdit && (
                  <button
                    className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      const agent = agents.find((a) => a.id === openMenuId);
                      if (agent) {
                        handleAction(() => onEdit(agent));
                      }
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                )}
                {onDelete && (
                  <button
                    className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      const agentToDelete = agents.find(
                        (a) => a.id === openMenuId,
                      );
                      if (agentToDelete) {
                        handleAction(() => onDelete(agentToDelete.id));
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
